// Session AMT: pure sort/filter logic for the office Call-outs list (CalloutsPage.tsx). Split out
// of the page component (already very large) so the URL <-> state parsing, the per-column sort
// accessors and the AND filter predicate are unit tested directly, with no rendering involved.
//
// Sorting reuses the platform's existing column-sort primitives (useSortableColumn.ts /
// registerTable.ts, Session AU): the same three-step asc/desc/default cycle, the same nulls-last,
// numeric-aware compare every other office list table already uses (Contracts, Defects, Service
// records, Projects, Renewals). Only the PERSISTENCE differs here: this session's brief wants a
// reloadable/shareable filtered view, so the chosen sort lives in the URL query string rather than
// useSortableColumn's sessionStorage. The comparison primitives are identical either way.
import type { CalloutListItem } from "../../lib/api";
import type { SortAccessors, SortState } from "../../lib/useSortableColumn";

// The full JobStatus enum a CallOut.status can hold (prisma/schema.prisma). Declared once here so
// the status filter pills and the priority sort agree on the complete set rather than each
// re-deriving it from whatever the list happens to contain today. `follow_up` was retired (Session
// AQY, D-AQX-1): it was never a producible status, so it is dropped here along with the enum value
// rather than left as a dead filter pill nobody's call-out can ever match.
export const CALLOUT_STATUSES = [
  "scheduled",
  "in_progress",
  "parts_pending",
  "pending_approval",
  "complete",
  "cancelled",
] as const;
export type CalloutStatus = (typeof CALLOUT_STATUSES)[number];

function isCalloutStatus(v: string): v is CalloutStatus {
  return (CALLOUT_STATUSES as readonly string[]).includes(v);
}

// "Outstanding" (the default view): every status except the two terminal ones. A completed or
// cancelled call-out needs nothing further from anyone, which is the office's own working
// definition of "done" (mirrors the platform two-step-delete/archive split elsewhere: closed
// states hide by default, everything else is live).
export const OUTSTANDING_STATUSES: CalloutStatus[] = CALLOUT_STATUSES.filter((s) => s !== "complete" && s !== "cancelled");

// The natural workflow order for the Status column sort -- not alphabetical, which would put
// "cancelled" ahead of "scheduled" and tell the office nothing useful. Not-yet-attended first,
// then active work, then blocked-but-still-open states, then awaiting office paperwork, then the
// two closed states last: the same "outstanding vs closed" split the default filter uses.
const STATUS_PRIORITY: Record<string, number> = {
  scheduled: 0,
  in_progress: 1,
  parts_pending: 2,
  pending_approval: 3,
  complete: 4,
  cancelled: 5,
};

export const STATUS_LABEL: Record<CalloutStatus, string> = {
  scheduled: "Scheduled",
  in_progress: "In progress",
  parts_pending: "On hold",
  pending_approval: "Closed, awaiting release",
  complete: "Complete",
  cancelled: "Cancelled",
};

// Column sort accessors. sortRows() compares these with compareSortValues, which already sorts
// null/blank values LAST regardless of direction and compares strings numeric-aware (so "ELE-9"
// sorts before "ELE-10") -- Reference and Lift both lean on that rather than parsing a number out
// by hand. Raised/Attended are ISO instants, which compare correctly as plain strings.
export const CALLOUT_SORT: SortAccessors<CalloutListItem> = {
  reference: (c) => c.reference ?? c.ref,
  site: (c) => c.siteName,
  lift: (c) => c.assetRef,
  customer: (c) => c.customerName,
  fault: (c) => c.faultDescription,
  raised: (c) => c.reportedAt,
  attended: (c) => c.attendedAt,
  status: (c) => STATUS_PRIORITY[c.status] ?? 99,
};

export function parseSortState(params: URLSearchParams): SortState {
  const key = params.get("sort");
  const dir = params.get("dir");
  if (key && (dir === "asc" || dir === "desc")) return { sortKey: key, sortDir: dir };
  return { sortKey: null, sortDir: "asc" };
}

export interface CalloutFilterState {
  statuses: Set<CalloutStatus>; // ignored when allStatuses is true
  allStatuses: boolean; // true = every status matches, including any value outside the known enum
  statusIsDefault: boolean; // true only when the URL carried no `status` param at all
  siteKey: string | null; // c.siteId ?? c.siteName, or null for "any site"
  from: string | null; // "YYYY-MM-DD", inclusive
  to: string | null; // "YYYY-MM-DD", inclusive
  q: string; // free text against Reference / Customer / Fault; "" = no filter
}

// The opaque grouping key for a row's site: the real id when the call-out is linked to a site on
// the register, else the free-text site name (an ad-hoc emergency call-out deliberately keeps no
// Customer/Site record at all -- see CalloutsPage's own comments). Never displayed as-is;
// deriveSiteOptions pairs it with the label the office actually reads.
export function siteKeyOf(c: CalloutListItem): string {
  return c.siteId ?? c.siteName;
}

// Parse the URL query string into a resolved filter state. No `status` param means the default
// outstanding view; `status=all` means every status (including any future value outside the known
// enum, so a status the office has never seen a filter for still cannot go missing); an explicit
// comma-separated list is validated against the known enum, dropping anything unrecognised.
export function parseCalloutFilters(params: URLSearchParams): CalloutFilterState {
  const statusParam = params.get("status");
  let statuses: Set<CalloutStatus>;
  let allStatuses: boolean;
  let statusIsDefault: boolean;
  if (statusParam == null) {
    statuses = new Set(OUTSTANDING_STATUSES);
    allStatuses = false;
    statusIsDefault = true;
  } else if (statusParam === "all") {
    statuses = new Set(CALLOUT_STATUSES);
    allStatuses = true;
    statusIsDefault = false;
  } else {
    statuses = new Set(
      statusParam
        .split(",")
        .map((s) => s.trim())
        .filter(isCalloutStatus),
    );
    allStatuses = false;
    statusIsDefault = false;
  }
  return {
    statuses,
    allStatuses,
    statusIsDefault,
    siteKey: params.get("site") || null,
    from: params.get("from") || null,
    to: params.get("to") || null,
    q: params.get("q") ?? "",
  };
}

// Whether the office has actively narrowed the view, for the "Clear all filters" affordance. The
// implicit outstanding default is deliberately NOT counted here, and neither is status=all (which
// narrows nothing): both read as "office hasn't touched status" from this function's point of
// view. The default's own narrowing is communicated separately and permanently by the filter
// bar's "Showing outstanding call-outs only... Show all" note (driven by statusIsDefault
// directly), so a pristine, untouched page load shows that ONE explanation rather than an
// identical-sounding "Clear all filters (1)" button next to it before the office has done
// anything. An EXPLICIT status choice -- a narrower subset, or even an explicit empty selection --
// still counts, because that is the office actively narrowing the view, exactly the same as
// picking a site or a date range.
function statusIsActive(state: CalloutFilterState): boolean {
  return !state.statusIsDefault && !state.allStatuses;
}

export function hasActiveFilters(state: CalloutFilterState): boolean {
  return statusIsActive(state) || state.siteKey != null || state.from != null || state.to != null || state.q.trim() !== "";
}

export function activeFilterCount(state: CalloutFilterState): number {
  return (
    (statusIsActive(state) ? 1 : 0) +
    (state.siteKey != null ? 1 : 0) +
    (state.from != null ? 1 : 0) +
    (state.to != null ? 1 : 0) +
    (state.q.trim() !== "" ? 1 : 0)
  );
}

// Local (device-timezone) YYYY-MM-DD for an ISO instant, matching how this page's own fmtDate()
// already displays the Raised/Attended columns (device zone, no explicit UTC override). A UTC
// slice would disagree with what the office sees printed in the Raised column for the seven
// months of BST, so the range filter derives the day the same way the column that shows it does.
function localDateKey(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function matchesSearch(c: CalloutListItem, qLower: string): boolean {
  if (!qLower) return true;
  const haystacks = [c.reference ?? c.ref, c.customerName, c.faultDescription ?? ""];
  return haystacks.some((h) => h.toLowerCase().includes(qLower));
}

// The AND filter: every active dimension must match. Pure, and independent of sort order (the
// page calls this before sortRows). A status set that can genuinely be empty (the office
// deselected every pill) correctly matches nothing rather than silently falling back to "show
// everything" -- an unusual state, but an honest and reversible one (Clear all restores the
// default).
export function filterCallouts(rows: CalloutListItem[], state: CalloutFilterState): CalloutListItem[] {
  const qLower = state.q.trim().toLowerCase();
  return rows.filter((c) => {
    if (!state.allStatuses && !state.statuses.has(c.status as CalloutStatus)) return false;
    if (state.siteKey != null && siteKeyOf(c) !== state.siteKey) return false;
    if (state.from || state.to) {
      const day = localDateKey(c.reportedAt);
      if (!day) return false;
      if (state.from && day < state.from) return false;
      if (state.to && day > state.to) return false;
    }
    if (!matchesSearch(c, qLower)) return false;
    return true;
  });
}

// ---- Session SC2C: the same filters and sort, asked of the server ---------------------------------------------------------------------
// The list is 50 rows at a time; the filters above are what the person chose, and the server applies them (GET /api/callouts, server/src/
// modules/callouts/calloutListQuery.ts). filterCallouts and CALLOUT_SORT stay as the reference the server is held equal to.
export const CALLOUT_PAGE_SIZE = 50;
const SERVER_SORT_KEYS = ["reference", "site", "lift", "customer", "fault", "raised", "attended", "status"];

export interface CalloutPageQuery {
  /** True when the person deselected every status: nothing matches, and the server is not asked. */
  empty: boolean;
  status?: string;
  siteId?: string;
  from?: string;
  to?: string;
  q?: string;
  sort?: string;
  dir?: "asc" | "desc";
}

/** The server filter and sort for a resolved filter state and sort. `q` is the (debounced) search text to send. */
export function calloutPageQuery(filter: CalloutFilterState, sort: SortState, q: string): CalloutPageQuery {
  const out: CalloutPageQuery = { empty: !filter.allStatuses && filter.statuses.size === 0 };
  if (filter.allStatuses) out.status = "all";
  else if (filter.statuses.size > 0) out.status = CALLOUT_STATUSES.filter((s) => filter.statuses.has(s)).join(",");
  if (filter.siteKey) out.siteId = filter.siteKey;
  if (filter.from) out.from = filter.from;
  if (filter.to) out.to = filter.to;
  if (q.trim()) out.q = q.trim();
  if (sort.sortKey && SERVER_SORT_KEYS.includes(sort.sortKey)) {
    out.sort = sort.sortKey;
    out.dir = sort.sortDir;
  }
  return out;
}
